import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { Window, Dialog, ConfirmDialog, EmptyState, Loading } from "./VB";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";

export default function SimpleCRUD({ title, endpoint, fields, testidPrefix }) {
  const [items, setItems] = useState([]);
  const [refs, setRefs] = useState({});
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({});
  const [del, setDel] = useState(null);

  const empty = () => fields.reduce((a, f) => ({ ...a, [f.key]: f.default ?? "" }), {});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(endpoint);
      setItems(data);
      const refData = {};
      for (const f of fields) {
        if (f.refEndpoint) {
          const r = await api.get(f.refEndpoint);
          refData[f.key] = r.data;
        }
      }
      setRefs(refData);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [endpoint]);

  const openCreate = () => { setForm(empty()); setEditId(null); setOpen(true); };
  const openEdit = (it) => { setForm({ ...empty(), ...it }); setEditId(it.id); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      for (const f of fields) {
        if (f.type === "number") payload[f.key] = parseFloat(payload[f.key] || 0);
        if (f.type === "checkbox") payload[f.key] = !!payload[f.key];
      }
      if (editId) await api.put(`${endpoint}/${editId}`, payload);
      else await api.post(endpoint, payload);
      toast.success("Data tersimpan");
      setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`${endpoint}/${del.id}`);
      toast.success("Data dihapus"); setDel(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const renderCell = (it, f) => {
    if (f.refEndpoint) {
      const opts = refs[f.key] || [];
      const found = opts.find((o) => o.id === it[f.key]);
      return found?.[f.refLabel || "nama"] || "-";
    }
    if (f.type === "checkbox") return it[f.key] ? "Aktif" : "Nonaktif";
    return it[f.key] || "-";
  };

  return (
    <Window
      title={title}
      actions={
        <button className="vb-btn" onClick={openCreate} data-testid={`${testidPrefix}-add`}>
          <Plus className="w-3 h-3 inline mr-1" /> Tambah
        </button>
      }
    >
      {loading ? <Loading /> : items.length === 0 ? <EmptyState /> : (
        <div style={{ overflowX: "auto" }}>
          <table className="vb-table">
            <thead>
              <tr>
                {fields.filter((f) => !f.hideInTable).map((f) => <th key={f.key}>{f.label}</th>)}
                <th style={{ width: 110 }}>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id}>
                  {fields.filter((f) => !f.hideInTable).map((f) => <td key={f.key}>{renderCell(it, f)}</td>)}
                  <td>
                    <button className="vb-btn" style={{ padding: "2px 6px", marginRight: 4 }} onClick={() => openEdit(it)} data-testid={`${testidPrefix}-edit-${it.id}`}>
                      <Pencil className="w-3 h-3" />
                    </button>
                    <button className="vb-btn-danger" style={{ padding: "2px 6px" }} onClick={() => setDel(it)} data-testid={`${testidPrefix}-delete-${it.id}`}>
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        title={editId ? `Edit ${title}` : `Tambah ${title}`}
        width={550}
        footer={
          <>
            <button className="vb-btn" onClick={() => setOpen(false)} data-testid={`${testidPrefix}-cancel`}>Batal</button>
            <button className="vb-btn-primary" onClick={submit} data-testid={`${testidPrefix}-save`}>Simpan</button>
          </>
        }
      >
        <form onSubmit={submit}>
          {fields.map((f) => (
            <div key={f.key} className="vb-form-row">
              <label className="vb-label">{f.label}{f.required && "*"}</label>
              {f.type === "select" || f.refEndpoint ? (
                <select className="vb-select" value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} required={f.required} data-testid={`${testidPrefix}-${f.key}`}>
                  <option value="">- pilih -</option>
                  {(f.options || refs[f.key] || []).map((o) => (
                    <option key={o.id || o} value={o.id || o}>{o[f.refLabel || "nama"] || o}</option>
                  ))}
                </select>
              ) : f.type === "textarea" ? (
                <textarea className="vb-textarea" rows={2} value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} data-testid={`${testidPrefix}-${f.key}`} />
              ) : f.type === "checkbox" ? (
                <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
                  <input type="checkbox" checked={!!form[f.key]} onChange={(e) => setForm({ ...form, [f.key]: e.target.checked })} data-testid={`${testidPrefix}-${f.key}`} />
                  {f.checkboxLabel || "Aktif"}
                </label>
              ) : (
                <input type={f.type || "text"} className="vb-input" value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} required={f.required} step={f.type === "number" ? "any" : undefined} data-testid={`${testidPrefix}-${f.key}`} />
              )}
            </div>
          ))}
        </form>
      </Dialog>

      <ConfirmDialog
        open={!!del}
        onClose={() => setDel(null)}
        onConfirm={doDelete}
        message="Yakin ingin menghapus data ini?"
      />
    </Window>
  );
}
